import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    categories: {
        /** Keyboard shortcuts category label for locate actions */
        locate: string;
        /** Keyboard shortcuts category label for panel actions */
        panels: string;
        /** Keyboard shortcuts category label for manipulation actions */
        manipulate: string;
        /** Keyboard shortcuts category label for object navigation */
        objects: string;
        /** Keyboard shortcuts category label for viewing actions */
        viewing: string;
        /** Keyboard shortcuts category label for comment actions */
        comments: string;
        /** Keyboard shortcuts category label for actions on an anchored comment pin */
        anchoredComments: string;
    };
    categorySubtitles: {
        /** Subtitle under the anchored comments category in the keyboard shortcuts dialog */
        anchoredComments: string;
    };
    labels: {
        /** Keyboard shortcut label for keyboard shortcuts help */
        keyboardShortcutsHelp: string;
        /** Keyboard shortcut label for in-project search */
        inProjectSearch: string;
        /** Keyboard shortcut label for quick search */
        quickSearch: string;
        /** Keyboard shortcut label for submitting feedback */
        submitFeatureRequest: string;
        /** Keyboard shortcut label for priming project navigation panel */
        primeProjectNavigationPanel: string;
        /** Keyboard shortcut label for priming main panel */
        primeMainPanel: string;
        /** Keyboard shortcut label for priming viewer panel */
        primeViewerPanel: string;
        /** Keyboard shortcut label for toggling viewer panel */
        toggleViewerPanel: string;
        /** Keyboard shortcut label for priming details panel */
        primeDetailsPanel: string;
        /** Keyboard shortcut label for toggling viewer */
        toggleViewer: string;
        /** Keyboard shortcut label for toggling details panel */
        toggleDetailsPanel: string;
        /** Keyboard shortcut label for toggling AI assistant panel */
        toggleAiAssistantPanel: string;
        /** Keyboard shortcut label for priming AI assistant panel */
        primeAiPanel: string;
        /** Keyboard shortcut label for toggling floating prompt bar */
        toggleFloatingPromptBar: string;
        /** Keyboard shortcut label for renaming */
        rename: string;
        /** Keyboard shortcut label for removing rating */
        removeRating: string;
        /** Keyboard shortcut label for rating one star */
        rate1Star: string;
        /** Keyboard shortcut label for rating two stars */
        rate2Stars: string;
        /** Keyboard shortcut label for rating three stars */
        rate3Stars: string;
        /** Keyboard shortcut label for rating four stars */
        rate4Stars: string;
        /** Keyboard shortcut label for rating five stars */
        rate5Stars: string;
        /** Keyboard shortcut label for exiting a folder */
        exitFolder: string;
        /** Keyboard shortcut label for moving to asset up */
        goToAssetUp: string;
        /** Keyboard shortcut label for moving up and selecting range */
        goToAssetUpAndSelectRange: string;
        /** Keyboard shortcut label for moving to first item */
        goToFirst: string;
        /** Keyboard shortcut label for moving to first item and selecting range */
        goToFirstAndSelectRange: string;
        /** Keyboard shortcut label for moving to asset left */
        goToAssetLeft: string;
        /** Keyboard shortcut label for moving left and selecting range */
        goToAssetLeftAndSelectRange: string;
        /** Keyboard shortcut label for moving right and selecting range */
        goToAssetRightAndSelectRange: string;
        /** Keyboard shortcut label for moving to asset right */
        goToAssetRight: string;
        /** Keyboard shortcut label for moving to last item and selecting range */
        goToLastAndSelectRange: string;
        /** Keyboard shortcut label for moving to last item */
        goToLast: string;
        /** Keyboard shortcut label for moving down and selecting range */
        goToAssetDownAndSelectRange: string;
        /** Keyboard shortcut label for moving to asset down */
        goToAssetDown: string;
        /** Keyboard shortcut label for opening folder in asset carousel */
        openFolderInAssetCarousel: string;
        /** Keyboard shortcut label for opening asset or folder in grid */
        openAssetOrFolderInGrid: string;
        /** Keyboard shortcut label for previewing the highlighted asset or folder */
        quickLook: string;
        /** Keyboard shortcut label for opening an asset in a new tab */
        openInNewTab: string;
        /** Keyboard shortcut label for selecting all */
        selectAll: string;
        /** Keyboard shortcut label for deselecting all */
        deselectAll: string;
        /** Keyboard shortcut label for toggling checkbox */
        toggleCheckbox: string;
        /** Keyboard shortcut label for downloading selection */
        download: string;
        /** Keyboard shortcut label for duplicating selection */
        duplicate: string;
        /** Keyboard shortcut label for cutting selection */
        cutSelection: string;
        /** Keyboard shortcut label for copying selection */
        copySelection: string;
        /** Keyboard shortcut label for pasting clipboard */
        pasteClipboard: string;
        /** Keyboard shortcut label for deleting selection */
        delete: string;
        /** Keyboard shortcut label for creating a folder */
        createFolder: string;
        /** Keyboard shortcut label for uploading assets */
        uploadAssets: string;
        /** Keyboard shortcut label for uploading a folder */
        uploadFolder: string;
        /** Keyboard shortcut label for creating a version stack */
        createVersionStack: string;
        /** Keyboard shortcut label for comparing versions */
        compareVersions: string;
        /** Keyboard shortcut label for next asset */
        goToNextAsset: string;
        /** Keyboard shortcut tooltip for next asset */
        goToNextAssetTooltip: string;
        /** Keyboard shortcut label for previous asset */
        goToPreviousAsset: string;
        /** Keyboard shortcut tooltip for previous asset */
        goToPreviousAssetTooltip: string;
        /** Keyboard shortcut label for fullscreen toggle */
        fullscreen: string;
        /** Keyboard shortcut label for quick toggle in comparison viewer */
        quickToggle: string;
        /** Keyboard shortcut label for zoom in */
        zoomIn: string;
        /** Keyboard shortcut label for zoom out */
        zoomOut: string;
        /** Keyboard shortcut label for zoom fit */
        zoomFit: string;
        /** Keyboard shortcut label for zoom fill */
        zoomFill: string;
        /** Keyboard shortcut label for zoom to 100 percent */
        zoomToHundred: string;
        /** Keyboard shortcut label for play or pause */
        playPause: string;
        /** Keyboard shortcut label for playing reverse at multiple speeds */
        playReverseMultipleSpeeds: string;
        /** Keyboard shortcut label for playing forward at multiple speeds */
        playForwardMultipleSpeeds: string;
        /** Keyboard shortcut label for incrementally playing reverse at multiple speeds */
        playIncrementalReverseMultipleSpeeds: string;
        /** Keyboard shortcut label for incrementally playing forward at multiple speeds */
        playIncrementalForwardMultipleSpeeds: string;
        /** Keyboard shortcut label for loop playback */
        playLoop: string;
        /** Keyboard shortcut label for moving one frame back */
        frameBack: string;
        /** Keyboard shortcut label for moving ten frames back */
        framesBack10: string;
        /** Keyboard shortcut label for moving ten frames forward */
        framesForward10: string;
        /** Keyboard shortcut label for moving one frame forward */
        frameForward: string;
        /** Keyboard shortcut label for setting in point */
        markInPoint: string;
        /** Keyboard shortcut label for setting out point */
        markOutPoint: string;
        /** Keyboard shortcut label for going to in point */
        goToInPoint: string;
        /** Keyboard shortcut label for going to out point */
        goToOutPoint: string;
        /** Keyboard shortcut label for toggling guides */
        guides: string;
        /** Keyboard shortcut label for marking range */
        markRange: string;
        /** Keyboard shortcut label for mute toggle */
        mute: string;
        /** Keyboard shortcut label for toggling transcript and captions panel */
        toggleTranscriptCaptionsPanel: string;
        /** Keyboard shortcut label for increasing volume by 10 percent */
        volumeUp10Percent: string;
        /** Keyboard shortcut label for decreasing volume by 10 percent */
        volumeDown10Percent: string;
        /** Keyboard shortcut label for marquee zoom */
        marqueeZoom: string;
        /** Keyboard shortcut label for pan tool */
        pan: string;
        /** Keyboard shortcut label for magnify tool */
        magnify: string;
        /** Keyboard shortcut label for previous document page */
        goToPreviousPageInDocument: string;
        /** Keyboard shortcut label for next document page */
        goToNextPageInDocument: string;
        /** Keyboard shortcut label for scrolling up in document */
        scrollUpInDocument: string;
        /** Keyboard shortcut label for scrolling down in document */
        scrollDownInDocument: string;
        /** Keyboard shortcut label for finding in document */
        findInDocument: string;
        /** Keyboard shortcut label for closing in-document search */
        closeInDocumentSearch: string;
        /** Keyboard shortcut label for finding in transcript */
        findInTranscript: string;
        /** Keyboard shortcut label for canceling transcription edit */
        cancelTranscriptionEdit: string;
        /** Keyboard shortcut label for adding a comment */
        addComment: string;
        /** Keyboard shortcut label for adding an anchored comment */
        addAnchoredComment: string;
        /** Keyboard shortcut label for moving an anchored comment pin left */
        movePinLeft: string;
        /** Keyboard shortcut label for moving an anchored comment pin right */
        movePinRight: string;
        /** Keyboard shortcut label for moving an anchored comment pin up */
        movePinUp: string;
        /** Keyboard shortcut label for moving an anchored comment pin down */
        movePinDown: string;
        /** Keyboard shortcut label for switching an anchored comment pin to larger steps */
        largePinSteps: string;
        /** Keyboard shortcut label for switching an anchored comment pin to one pixel steps */
        precisePinSteps: string;
        /** Keyboard shortcut label for finishing a move of an anchored comment pin */
        finishPinMove: string;
        /** Keyboard shortcut label for cancelling a pin move */
        cancelPinMove: string;
        /** Keyboard shortcut label for adding an annotation */
        addAnnotation: string;
        /** Keyboard shortcut label for replying to comment */
        replyToComment: string;
        /** Keyboard shortcut label for undo annotation */
        undoAnnotation: string;
        /** Keyboard shortcut label for redo annotation */
        redoAnnotation: string;
    };
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            categories: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Keyboard shortcuts category label for locate actions */
                        locate: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.locate?c=hyrzepdo9mz9", defaultMessage: "Locate" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for panel actions */
                        panels: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.panels?c=3ajj7ueluk913", defaultMessage: "Panels" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for manipulation actions */
                        manipulate: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.manipulate?c=d57tw55x6kec", defaultMessage: "Manipulate" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for object navigation */
                        objects: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.objects?c=rjm4chdrkzsh", defaultMessage: "Objects" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for viewing actions */
                        viewing: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.viewing?c=33c7gyzbmewwt", defaultMessage: "Viewing" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for comment actions */
                        comments: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.comments?c=30ob2za738yys", defaultMessage: "Comments" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcuts category label for actions on an anchored comment pin */
                        anchoredComments: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categories.anchoredComments?c=1rza2mmmx6qdi", defaultMessage: "Anchored comments" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            categorySubtitles: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Subtitle under the anchored comments category in the keyboard shortcuts dialog */
                        anchoredComments: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.categorySubtitles.anchoredComments?c=277vtttuxr8pj", defaultMessage: "Applies while a comment pin is focused" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            labels: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Keyboard shortcut label for keyboard shortcuts help */
                        keyboardShortcutsHelp: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.keyboardShortcutsHelp?c=38uu0s2xrbl5s", defaultMessage: "Keyboard shortcuts help" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for in-project search */
                        inProjectSearch: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.inProjectSearch?c=prggdki6nhmo", defaultMessage: "In-Project search" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for quick search */
                        quickSearch: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.quickSearch?c=rvw6wvg4upb4", defaultMessage: "Quick search" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for submitting feedback */
                        submitFeatureRequest: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.submitFeatureRequest?c=5adg65suwfs8", defaultMessage: "Submit Feedback" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for priming project navigation panel */
                        primeProjectNavigationPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.primeProjectNavigationPanel?c=63xkfkx5i8hv", defaultMessage: "Prime project navigation panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for priming main panel */
                        primeMainPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.primeMainPanel?c=2769ybwkjdpgr", defaultMessage: "Prime main panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for priming viewer panel */
                        primeViewerPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.primeViewerPanel?c=c28bggtirlmj", defaultMessage: "Prime viewer panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling viewer panel */
                        toggleViewerPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleViewerPanel?c=1v6x4anqw8np3", defaultMessage: "Toggle viewer panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for priming details panel */
                        primeDetailsPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.primeDetailsPanel?c=mkhqauo5ob0a", defaultMessage: "Prime details panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling viewer */
                        toggleViewer: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleViewer?c=330g5d9sbsvh2", defaultMessage: "Toggle viewer" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling details panel */
                        toggleDetailsPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleDetailsPanel?c=26mbeaj6gd237", defaultMessage: "Toggle details panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling AI assistant panel */
                        toggleAiAssistantPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleAiAssistantPanel?c=2pklpviddkihy", defaultMessage: "Toggle AI assistant panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for priming AI assistant panel */
                        primeAiPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.primeAiPanel?c=f0zerue1am1y", defaultMessage: "Prime AI assistant panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling floating prompt bar */
                        toggleFloatingPromptBar: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleFloatingPromptBar?c=tv4prccnrt21", defaultMessage: "Toggle floating prompt bar" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for renaming */
                        rename: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rename?c=2xvuf86b5nq1g", defaultMessage: "Rename" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for removing rating */
                        removeRating: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.removeRating?c=1uuvhs2sf5lo8", defaultMessage: "Remove rating" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for rating one star */
                        rate1Star: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rate1Star?c=38jz0fz8nwz51", defaultMessage: "Rate 1 star" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for rating two stars */
                        rate2Stars: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rate2Stars?c=1zxmr86fx22l", defaultMessage: "Rate 2 stars" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for rating three stars */
                        rate3Stars: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rate3Stars?c=3aawnblclnapl", defaultMessage: "Rate 3 stars" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for rating four stars */
                        rate4Stars: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rate4Stars?c=1t4ucb7yyispx", defaultMessage: "Rate 4 stars" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for rating five stars */
                        rate5Stars: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.rate5Stars?c=233dwnw7qem9g", defaultMessage: "Rate 5 stars" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for exiting a folder */
                        exitFolder: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.exitFolder?c=1n6ndc95kdosx", defaultMessage: "Exit folder" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to asset up */
                        goToAssetUp: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetUp?c=18qhnwhql5cu8", defaultMessage: "Go to asset up" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving up and selecting range */
                        goToAssetUpAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetUpAndSelectRange?c=2fpa30tei2k1v", defaultMessage: "Go to asset up and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to first item */
                        goToFirst: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToFirst?c=1y94oad8fecva", defaultMessage: "Go to first" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to first item and selecting range */
                        goToFirstAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToFirstAndSelectRange?c=o3xfkk0m2r6f", defaultMessage: "Go to first and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to asset left */
                        goToAssetLeft: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetLeft?c=26zt30aohn7wd", defaultMessage: "Go to asset left" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving left and selecting range */
                        goToAssetLeftAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetLeftAndSelectRange?c=2vmsux9rieoof", defaultMessage: "Go to asset left and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving right and selecting range */
                        goToAssetRightAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetRightAndSelectRange?c=321ikapr2o3sw", defaultMessage: "Go to asset right and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to asset right */
                        goToAssetRight: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetRight?c=1b1n2z3ue21lx", defaultMessage: "Go to asset right" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to last item and selecting range */
                        goToLastAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToLastAndSelectRange?c=21moz8o9ndj3o", defaultMessage: "Go to last and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to last item */
                        goToLast: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToLast?c=25ig1geq0fzho", defaultMessage: "Go to last" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving down and selecting range */
                        goToAssetDownAndSelectRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetDownAndSelectRange?c=ynj7vci3ezqo", defaultMessage: "Go to asset down and select range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving to asset down */
                        goToAssetDown: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToAssetDown?c=3ndq2gvqns2e8", defaultMessage: "Go to asset down" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for opening folder in asset carousel */
                        openFolderInAssetCarousel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.openFolderInAssetCarousel?c=voqzyfulug82", defaultMessage: "Open folder in the asset carousel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for opening asset or folder in grid */
                        openAssetOrFolderInGrid: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.openAssetOrFolderInGrid?c=3h3vs5wjqo34h", defaultMessage: "Open asset or folder in the grid" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for previewing the highlighted asset or folder */
                        quickLook: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.quickLook?c=12dwd74jj58ra", defaultMessage: "Preview" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for opening an asset in a new tab */
                        openInNewTab: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.openInNewTab?c=15sjcxiccofsk", defaultMessage: "Open in new tab" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for selecting all */
                        selectAll: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.selectAll?c=1im7y3ez45r0a", defaultMessage: "Select all" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for deselecting all */
                        deselectAll: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.deselectAll?c=2fuewccee8wxi", defaultMessage: "Deselect all" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling checkbox */
                        toggleCheckbox: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleCheckbox?c=324j3tqcljl88", defaultMessage: "Toggle checkbox" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for downloading selection */
                        download: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.download?c=82r1papihj9v", defaultMessage: "Download" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for duplicating selection */
                        duplicate: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.duplicate?c=gwcnintdwpnx", defaultMessage: "Duplicate" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for cutting selection */
                        cutSelection: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.cutSelection?c=27vmazkg0wi0u", defaultMessage: "Cut selection" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for copying selection */
                        copySelection: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.copySelection?c=2i9omvll7znp2", defaultMessage: "Copy selection" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for pasting clipboard */
                        pasteClipboard: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.pasteClipboard?c=ew5kngmjx1hg", defaultMessage: "Paste clipboard" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for deleting selection */
                        delete: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.delete?c=7nzkz7owpk0x", defaultMessage: "Delete" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for creating a folder */
                        createFolder: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.createFolder?c=138gwfj10yexk", defaultMessage: "Create folder" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for uploading assets */
                        uploadAssets: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.uploadAssets?c=2r2ily5gfaabg", defaultMessage: "Upload Asset" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for uploading a folder */
                        uploadFolder: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.uploadFolder?c=31z0qfh0ynkyo", defaultMessage: "Upload Folder" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for creating a version stack */
                        createVersionStack: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.createVersionStack?c=2m71m9b4dkfx6", defaultMessage: "Create Version Stack" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for comparing versions */
                        compareVersions: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.compareVersions?c=rne3fi87crsk", defaultMessage: "Compare versions" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for next asset */
                        goToNextAsset: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToNextAsset?c=1xzzd7073pa5k", defaultMessage: "Go to next asset" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut tooltip for next asset */
                        goToNextAssetTooltip: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToNextAssetTooltip?c=eebhofquox8g", defaultMessage: "For time-based assets, arrow right advances 1 frame forward" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for previous asset */
                        goToPreviousAsset: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToPreviousAsset?c=230sof6ebxtx", defaultMessage: "Go to previous asset" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut tooltip for previous asset */
                        goToPreviousAssetTooltip: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToPreviousAssetTooltip?c=2yty2z4b4l5hw", defaultMessage: "For time-based assets, arrow left steps 1 frame back" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for fullscreen toggle */
                        fullscreen: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.fullscreen?c=1u9gmr7aydhlk", defaultMessage: "Fullscreen" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for quick toggle in comparison viewer */
                        quickToggle: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.quickToggle?c=3crts04pdw90w", defaultMessage: "Quick toggle" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for zoom in */
                        zoomIn: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.zoomIn?c=36b9hhpba2oyq", defaultMessage: "Zoom in" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for zoom out */
                        zoomOut: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.zoomOut?c=oh88ztixxmsp", defaultMessage: "Zoom out" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for zoom fit */
                        zoomFit: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.zoomFit?c=3susnkqv8fmlv", defaultMessage: "Zoom fit" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for zoom fill */
                        zoomFill: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.zoomFill?c=3rthhk7uq2h8n", defaultMessage: "Zoom fill" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for zoom to 100 percent */
                        zoomToHundred: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.zoomToHundred?c=3nawzrifwbjk7", defaultMessage: "Zoom to 100%" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for play or pause */
                        playPause: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playPause?c=1pyats9hl0db5", defaultMessage: "Play / pause" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for playing reverse at multiple speeds */
                        playReverseMultipleSpeeds: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playReverseMultipleSpeeds?c=q1w4w6luelsj", defaultMessage: "Play reverse multiple speeds" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for playing forward at multiple speeds */
                        playForwardMultipleSpeeds: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playForwardMultipleSpeeds?c=2l8ijg2mhn0vb", defaultMessage: "Play forward multiple speeds" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for incrementally playing reverse at multiple speeds */
                        playIncrementalReverseMultipleSpeeds: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playIncrementalReverseMultipleSpeeds?c=tm06sy84x0y1", defaultMessage: "Play incremental reverse multiple speeds" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for incrementally playing forward at multiple speeds */
                        playIncrementalForwardMultipleSpeeds: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playIncrementalForwardMultipleSpeeds?c=1lrc07khpe5eb", defaultMessage: "Play incremental forward multiple speeds" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for loop playback */
                        playLoop: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.playLoop?c=1pimos8hehvis", defaultMessage: "Play loop" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving one frame back */
                        frameBack: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.frameBack?c=3jbwf3g3v0igt", defaultMessage: "1 frame back" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving ten frames back */
                        framesBack10: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.framesBack10?c=1c7chl5ljuoy2", defaultMessage: "10 frames back" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving ten frames forward */
                        framesForward10: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.framesForward10?c=2wh7ccbdfo3e", defaultMessage: "10 frames forward" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving one frame forward */
                        frameForward: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.frameForward?c=34ed9jr4zgqtv", defaultMessage: "1 frame forward" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for setting in point */
                        markInPoint: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.markInPoint?c=3gn0kmkrkcxfp", defaultMessage: "Mark in point" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for setting out point */
                        markOutPoint: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.markOutPoint?c=7l6kci6khb3l", defaultMessage: "Mark out point" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for going to in point */
                        goToInPoint: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToInPoint?c=svj25wse8z6n", defaultMessage: "Go to in point" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for going to out point */
                        goToOutPoint: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToOutPoint?c=yvv3jnz0rrl3", defaultMessage: "Go to out point" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling guides */
                        guides: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.guides?c=28d89zrjtlcak", defaultMessage: "Guides" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for marking range */
                        markRange: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.markRange?c=336xk3ljnz9ap", defaultMessage: "Mark range" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for mute toggle */
                        mute: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.mute?c=3d5i8721hpime", defaultMessage: "Mute" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for toggling transcript and captions panel */
                        toggleTranscriptCaptionsPanel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.toggleTranscriptCaptionsPanel?c=bicdtc6qetd2", defaultMessage: "Toggle Transcript & Captions panel" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for increasing volume by 10 percent */
                        volumeUp10Percent: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.volumeUp10Percent?c=29vpdejrexuk1", defaultMessage: "Volume up (10%)" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for decreasing volume by 10 percent */
                        volumeDown10Percent: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.volumeDown10Percent?c=26yrhc5drvim7", defaultMessage: "Volume down (10%)" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for marquee zoom */
                        marqueeZoom: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.marqueeZoom?c=3tpmrg3j4xv8d", defaultMessage: "Marquee zoom" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for pan tool */
                        pan: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.pan?c=14l2ijrygmt8", defaultMessage: "Pan" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for magnify tool */
                        magnify: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.magnify?c=3iqmkqy5vdxhc", defaultMessage: "Magnify" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for previous document page */
                        goToPreviousPageInDocument: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToPreviousPageInDocument?c=bfqdh95z2fct", defaultMessage: "Go to previous page in document" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for next document page */
                        goToNextPageInDocument: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.goToNextPageInDocument?c=2oi4t7q3gk28x", defaultMessage: "Go to next page in document" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for scrolling up in document */
                        scrollUpInDocument: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.scrollUpInDocument?c=v8rrm6lq4tqq", defaultMessage: "Scroll up in document" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for scrolling down in document */
                        scrollDownInDocument: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.scrollDownInDocument?c=1q3wwfzaqgd2o", defaultMessage: "Scroll down in document" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for finding in document */
                        findInDocument: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.findInDocument?c=2kwkvu2s6htrm", defaultMessage: "Find in document" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for closing in-document search */
                        closeInDocumentSearch: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.closeInDocumentSearch?c=27k1wwwhf13t", defaultMessage: "" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for finding in transcript */
                        findInTranscript: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.findInTranscript?c=1uzl0eb80vhkz", defaultMessage: "Find in transcript" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for canceling transcription edit */
                        cancelTranscriptionEdit: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.cancelTranscriptionEdit?c=27k1wwwhf13t", defaultMessage: "" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for adding a comment */
                        addComment: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.addComment?c=2srat88j00tj4", defaultMessage: "Add comment" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for adding an anchored comment */
                        addAnchoredComment: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.addAnchoredComment?c=3fkln7y2bh5fo", defaultMessage: "Add anchored comment" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving an anchored comment pin left */
                        movePinLeft: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.movePinLeft?c=24kjnmqk07vg", defaultMessage: "Move pin left" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving an anchored comment pin right */
                        movePinRight: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.movePinRight?c=2l52e98aq7duw", defaultMessage: "Move pin right" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving an anchored comment pin up */
                        movePinUp: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.movePinUp?c=3o7wtnp8qe8d5", defaultMessage: "Move pin up" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for moving an anchored comment pin down */
                        movePinDown: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.movePinDown?c=36427aqgw5x20", defaultMessage: "Move pin down" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for switching an anchored comment pin to larger steps */
                        largePinSteps: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.largePinSteps?c=3sp3878zawcd2", defaultMessage: "Bigger steps (hold Shift with an arrow)" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for switching an anchored comment pin to one pixel steps */
                        precisePinSteps: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.precisePinSteps?c=3dff0kjpi2g2y", defaultMessage: "One pixel steps (hold Option with an arrow)" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for finishing a move of an anchored comment pin */
                        finishPinMove: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.finishPinMove?c=79ae00cd2l0l", defaultMessage: "Finish moving pin" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for cancelling a pin move */
                        cancelPinMove: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.cancelPinMove?c=1auxe1dwvup7n", defaultMessage: "Cancel moving pin" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for adding an annotation */
                        addAnnotation: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.addAnnotation?c=3l34abhzz115", defaultMessage: "Add annotation" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for replying to comment */
                        replyToComment: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.replyToComment?c=1a0ijrllfjruz", defaultMessage: "Reply to comment" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for undo annotation */
                        undoAnnotation: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.undoAnnotation?c=24vqcewsamz8i", defaultMessage: "Undo annotation" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Keyboard shortcut label for redo annotation */
                        redoAnnotation: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "COMMAND_LIST.labels.redoAnnotation?c=ci7raugnxe8e", defaultMessage: "Redo annotation" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
